import React from 'react';
import { Box, Typography } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';

// Animation keyframes
const spin = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

const loaderFadeIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.8);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

// Loader container for centered loading state
const LoaderContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(2),
  animation: `${loaderFadeIn} 0.5s ease-out`,
  color: 'white',
  textAlign: 'center',
  minHeight: '200px',
  width: '100%',
}));

export interface LoadingSpinnerProps {
  title?: string;
  subtitle?: string;
  size?: 'small' | 'medium' | 'large';
  color?: string;
  backgroundColor?: string;
  fullScreen?: boolean;
}

export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  title = 'Loading Unity...',
  subtitle = 'Please wait while we prepare your experience',
  size = 'medium',
  color = 'white',
  backgroundColor = 'transparent',
  fullScreen = false,
}) => {
  const sizeConfig = {
    small: { width: 40, height: 40, borderWidth: 3 },
    medium: { width: 60, height: 60, borderWidth: 4 },
    large: { width: 80, height: 80, borderWidth: 5 },
  };

  const currentSize = sizeConfig[size];

  const StyledLoader = styled(Box)(() => ({
    width: currentSize.width,
    height: currentSize.height,
    border: `${currentSize.borderWidth}px solid ${color === 'white' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)'}`,
    borderTop: `${currentSize.borderWidth}px solid ${color}`,
    borderRadius: '50%',
    animation: `${spin} 1s linear infinite`,
    marginBottom: '8px',
  }));

  const containerStyle = {
    backgroundColor,
    color,
    ...(fullScreen && {
      position: 'fixed' as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 9999,
      minHeight: '100vh',
    }),
  };

  return (
    <LoaderContainer sx={containerStyle}>
      <StyledLoader />
      {title && (
        <Typography
          variant="h6"
          sx={{
            color,
            fontWeight: 500,
            fontSize: size === 'small' ? '1rem' : size === 'large' ? '1.5rem' : '1.25rem',
          }}
        >
          {title}
        </Typography>
      )}
      {subtitle && (
        <Typography
          variant="body2"
          sx={{
            color: color === 'white' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
            fontSize: size === 'small' ? '0.75rem' : '0.875rem',
          }}
        >
          {subtitle}
        </Typography>
      )}
    </LoaderContainer>
  );
};

// Convenient component variants
export const SmallLoadingSpinner: React.FC<Omit<LoadingSpinnerProps, 'size'>> = props => (
  <LoadingSpinner size="small" {...props} />
);

export const LargeLoadingSpinner: React.FC<Omit<LoadingSpinnerProps, 'size'>> = props => (
  <LoadingSpinner size="large" {...props} />
);

export const FullScreenLoader: React.FC<Omit<LoadingSpinnerProps, 'fullScreen'>> = props => (
  <LoadingSpinner fullScreen {...props} />
);

export default LoadingSpinner;
